<template>
	<form class="goal-form">
		<div>
			<LabeledInputField
				:blur-is-input="false"
				:disabled="disabled || disabledFormFields.name"
				:focus="isFocused( `title` )"
				:is-valid="!errors.title"
				:selection="focusedSelectionFor( `title` )"
				:validation-error="errorFor( `title` )"
				:value="purifiedTitle"
				:hint="translate( `titleFieldHint` )"
				:label="translate( `titleFieldLabel` )"
				name="title"
				:placeholder="translate( `titleFieldPlaceholder` )"
				@input="onTitleInput"
				@click="onTitleFocus"
			/>
		</div>
		<div v-if="!hiddenFormFields.assignedTeamId">
			<LabeledTeamDropDown
				class="goal-form__assigned-team"
				:disabled="disabled || disabledFormFields.assignedTeamId"
				:hint="translate( `assignedTeamIdFieldHint` )"
				:is-valid="!errors.assignedTeamId"
				:options="possibleTeamOptions"
				:validation-error="errorFor( `assignedTeamId` )"
				:value="assignedTeamId"
				:label="translate( `assignedTeamIdFieldLabel` )"
				name="assignedTeamId"
				@change="onAssignedTeamChanged"
			/>
		</div>
		<div>
			<LabeledConfidenceLevelDropDown
				:disabled="disabled || disabledFormFields.confidenceLevel"
				:is-valid="!errors.confidenceLevel"
				:validation-error="errorFor( `confidenceLevel` )"
				:value="confidenceLevel"
				:hint="translate( `confidenceLevelFieldHint` )"
				:label="translate( `confidenceLevelFieldLabel` )"
				name="confidenceLevel"
				@change="onConfidenceLevelChanged"
			/>
		</div>
		<div class="goal-form__split-row">
			<div>
				<LabeledCalendarPicker
					:disabled="disabled || disabledFormFields.startDate"
					:hint="startDateHint"
					:is-valid="!errors.startDate"
					:validation-error="errorFor( `startDate` )"
					:value="startDate"
					:label="translate( `startDateFieldLabel` )"
					name="startDate"
					@change="onStartDateChanged"
				/>
			</div>
			<div>
				<LabeledCalendarPicker
					:disabled="disabled || disabledFormFields.endDate"
					:hint="endDateHint"
					:is-valid="!errors.endDate"
					:validation-error="errorFor( `endDate` )"
					:value="endDate"
					:label="translate( `endDateFieldLabel` )"
					name="endDate"
					@change="onEndDateChanged"
				/>
			</div>
		</div>
		<div v-if="!hiddenFormFields.comment" class="divider" />
		<div v-if="!hiddenFormFields.comment" class="goal-form__comment-row">
			<LabeledTextareaField
				:disabled="disabled || disabledFormFields.comment"
				:focus="isFocused( `comment` )"
				:hint="translate( `commentFieldHint` )"
				input-class-name="goal-form__comment-input"
				:is-valid="!errors.comment"
				:selection="focusedSelectionFor( `comment` )"
				:validation-error="errorFor( `comment` )"
				:value="comment"
				name="comment"
				:placeholder="translate( `commentFieldPlaceholder` )"
				@input="onCommentInput"
				@click="onCommentFocus"
			>
				<template #label>
					<div class="goal-form__comment-labels">
						<label>{{ translate( `commentFieldLabel` ) }}</label>
						<label>{{ translate( `commentFieldOptionalLabel` ) }}</label>
					</div>
				</template>
			</LabeledTextareaField>
		</div>
	</form>
</template>

<script>
import DOMPurify from "dompurify";

import {
	FormMixin,
	LabeledInputField,
	LabeledConfidenceLevelDropDown,
	LabeledCalendarPicker,
	LabeledTeamDropDown,
	LabeledTextareaField
} from "@teaming/common-ui";

export default {
	name: "GoalForm",
	mixins: [ FormMixin, ],
	emits: [ "delete", "archive", "unarchive", ],
	components: {
		LabeledInputField,
		LabeledTeamDropDown,
		LabeledConfidenceLevelDropDown,
		LabeledCalendarPicker,
		LabeledTextareaField,
	},
	props: {
		title: {
			type: String,
			required: true,
		},
		possibleTeamOptions: {
			type: Array,
			required: true,
		},
		assignedTeamId: {
			type: String,
			required: true,
		},
		confidenceLevel: {
			type: String,
			required: true,
		},
		startDate: {
			type: String,
			required: true,
		},
		endDate: {
			type: String,
			required: true,
		},
		showDateHints: {
			type: Boolean,
			required: true,
		},
		comment: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			handleChange: null,
		};
	},
	computed: {
		/** see FormMixin **/
		// eslint-disable-next-line vue/no-unused-properties
		form() {
			const {
				formId,
				title,
				assignedTeamId,
				confidenceLevel,
				startDate,
				endDate,
				comment,
			} = this;

			return {
				formId,
				title,
				assignedTeamId,
				confidenceLevel,
				startDate,
				endDate,
				comment,
			};
		},
		disabledFormFields() {
			return {
				name: false,
				assignedTeamId: false,
				confidenceLevel: false,
				startDate: false,
				endDate: false,
				comment: false,
				/** see FormMixin **/
				...this.disabledFields,
			};
		},
		hiddenFormFields() {
			return {
				name: false,
				assignedTeamId: false,
				confidenceLevel: false,
				startDate: false,
				endDate: false,
				comment: false,
				/** see FormMixin **/
				...this.hiddenFields,
			};
		},
		purifiedTitle() {
			// remove any and all HTML tags
			return DOMPurify.sanitize( this.title, {
				ALLOWED_TAGS: [],
			} );
		},
		startDateHint() {
			return this.showDateHints ?
				this.translate( "startDateFieldHint" ) :
				"";
		},
		endDateHint() {
			return this.showDateHints ?
				this.translate( "endDateFieldHint" ) :
				"";
		},
	},
	methods: {
		translate( key, args = {} ) {
			return this.$t( `common-gokr.components.GoalForm.${ key }`, args );
		},
		onTitleInput( e ) {
			clearTimeout( this.handleChange );
			this.handleChange = setTimeout( () => {
				const title = e.target.value;
				this.raiseFormChange( {
					title,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "title", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 50 );
		},
		onTitleFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "title", selectionStart );
		},
		onAssignedTeamChanged( option ) {
			const { value: assignedTeamId, } = option;
			this.raiseFormChange( {
				assignedTeamId,
			} );
			this.raiseFocusChange( "assignedTeamId" );
		},
		onConfidenceLevelChanged( option ) {
			const { value: confidenceLevel, } = option;
			this.raiseFormChange( {
				confidenceLevel,
			} );
			this.raiseFocusChange( "confidenceLevel" );
		},
		/** @param {Date} startDate */
		onStartDateChanged( startDate ) {
			this.raiseFormChange( {
				startDate: this.$luxonify( startDate ).toFormat( "MM/dd/yyyy" ),
			} );
			this.raiseFocusChange( "startDate" );
		},
		/** @param {Date} endDate */
		onEndDateChanged( endDate ) {
			this.raiseFormChange( {
				endDate: this.$luxonify( endDate ).toFormat( "MM/dd/yyyy" ),
			} );
			this.raiseFocusChange( "endDate" );
		},
		onCommentInput( e ) {
			clearTimeout( this.handleChange );
			this.handleChange = setTimeout( () => {
				const comment = e.target.value;
				this.raiseFormChange( {
					comment,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "comment", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 50 );
		},
		onCommentFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "comment", selectionStart );
		},
	},
};
</script>

<style lang="scss" scoped>
$goalFormOptionalLabelColor: $color2-400;

.goal-form {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	// tiny padding that prevents the field outlines from being cut off
	padding: 0 0.2rem 0 0.2rem;
}

.goal-form__split-row {
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
	gap: 1rem;

	> div {
		flex: 1;
	}
}

.goal-form__action-row {
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
	gap: 1rem;

	@include for-size('md') {
		justify-content: flex-start;
	}
}

.goal-form .divider {
	height: 0.0625rem;
	margin: 0.5rem 0;
	width: 100%;
	background-color: $borderColor--dark;
}

.goal-form__comment-row {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
}

.goal-form__comment-labels {
	display: flex;
	flex-direction: row;
	justify-content: space-between;

	:nth-child(2) {
		color: $goalFormOptionalLabelColor;
	}
}
</style>

<style lang="scss">
.goal-form__comment-input {
	height: 7.5rem;
}
</style>
